import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { prepareAuthNextCookie } from '@/lib/auth-next';

export function Home() {
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // If user is already signed in, send them to /saved. /saved is wrapped in
  // ProtectedRoute which runs the onboarding gate — so a signed-in user with
  // onboarding incomplete still gets pushed through /onboarding. (Sending
  // them to /search bypassed the gate because /search is a public route.)
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session) {
        navigate('/saved', { replace: true });
      } else {
        setCheckingSession(false);
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    // The Home page is signup-flavored ("Welcome to Solem" / first-touch landing).
    // The /auth/callback decides the final identity via server is_new_account;
    // this event is just the funnel-intent marker.
    const firstTouch = analytics.getFirstTouch();
    analytics.track('signup_started', {
      method: 'google',
      from_utm_source: firstTouch.first_utm_source,
      from_utm_medium: firstTouch.first_utm_medium,
      from_utm_campaign: firstTouch.first_utm_campaign,
      from_utm_content: firstTouch.first_utm_content,
      landing_url: firstTouch.first_landing_url,
      referrer: firstTouch.first_referrer,
    });
    try {
      // Sign the pending destination (if any) into an HttpOnly cookie BEFORE
      // OAuth fires — the cookie rides through the Google round-trip and is
      // consumed inside /api/auth/post-callback (S3.4, plan §4.6).
      await prepareAuthNextCookie();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        console.error('Error signing in with Google:', error.message);
        setSigningIn(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setSigningIn(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-[100dvh] bg-neutral-50 flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign in · Solem</title>
        <meta name="description" content="Sign in to Solem — your personal web search engine. Save useful web content with the Chrome extension and find it later." />
        <link rel="canonical" href="https://app.solem.ai" />
        <meta property="og:title" content="Sign in · Solem" />
        <meta property="og:url" content="https://app.solem.ai" />
        <meta property="og:image" content="https://app.solem.ai/product-screenshot.png" />
      </Helmet>

      <div className="min-h-[100dvh] bg-neutral-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-sm shadow-sm ring-1 ring-black/5 p-8">
            <div className="flex flex-col items-center text-center">
              <img src="/logo-brand.svg" alt="Solem" className="w-12 h-12" />
              <h1 className="mt-4 text-2xl font-light tracking-tight text-neutral-900">
                Welcome to Solem
              </h1>
              <p className="mt-2 text-sm font-light text-neutral-700">
                Your personal web search engine.
              </p>
            </div>

            <Button
              onClick={handleGoogleSignIn}
              disabled={signingIn}
              variant="outline"
              className="mt-8 w-full h-11 rounded-sm bg-white border-neutral-300 hover:bg-neutral-50 flex items-center justify-center gap-2"
            >
              {signingIn ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <img src="/google.svg" alt="" className="w-5 h-5" aria-hidden="true" />
              )}
              <span className="font-semibold text-sm text-foreground">
                {signingIn ? 'Signing you in…' : 'Continue with Google'}
              </span>
            </Button>
          </div>

          <p className="mt-6 text-center text-xs text-neutral-600">
            By continuing, you agree to our{' '}
            <a
              href="https://www.solem.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-700 underline hover:text-neutral-900"
            >Terms</a>
            {' '}and{' '}
            <a
              href="https://www.solem.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-700 underline hover:text-neutral-900"
            >Privacy Policy</a>.
          </p>
        </div>
      </div>
    </>
  );
}
